import RestClient from "@/app/shared/services/restClient"; // Roadmap to get api
import { AxiosInstance } from "axios";
import Vue from "vue";

import store from "@/app/app-state";
import {
  IndexationTiers,
  CalculatedIndexation,
  IndexFamily,
  Indexation,
  Indice,
  ParamsOptions,
  Societe,
  Tarif,
  TarifGeneral,
  Tiers,
} from "../types/Indexation";

export default class IndexationStore {
  private restClient: AxiosInstance;

  constructor(baseUrl: string) {
    this.restClient = new RestClient(baseUrl).Instance;
  }

  /* Debut indice et indexation */
  /**
   * Recuperation de la liste des indices
   * @returns
   */
  async getListeIndices(): Promise<{ data: Indice[]; itemsCount: number }> {
    let data = [] as Indice[];
    let itemsCount = 0;

    const params = { companyCode: store.state.userContext.currentCompany, warehouseCode: store.state.userContext.currentWarehouse };

    await this.restClient
      .get(`/Indices`, { params })
      .then((response) => {
        if (response.status == 200) {
          data = response.data;
          itemsCount = response.data.length;
        }
      })
      .catch((error) => {
        Vue.$log.error(error);
      });

    return { data, itemsCount };
  }
  /**
   * Recuperation des indexations
   * @param indice
   * @returns
   */
  async getListeIndexations(indice: Indice): Promise<{ data: Indexation[]; itemsCount: number }> {
    let data = [] as Indexation[];
    let itemsCount = 0;

    const params = {
      companyCode: store.state.userContext.currentCompany,
      warehouseCode: store.state.userContext.currentWarehouse,
    };
    await this.restClient
      .get(`/Indices/${indice.id}/Indexations`, { params })
      .then((response) => {
        if (response.status == 200) {
          data = response.data;
          itemsCount = response.data.length;
        }
      })
      .catch((error) => {
        Vue.$log.error(error);
      });

    return { data, itemsCount };
  }

  /**
   * Ajout d'une indexation
   * @param indexation
   * @param indice
   * @returns
   */
  async addIndexation(indexation: Indexation, indice: Indice): Promise<Indexation> {
    let data: Indexation = {} as Indexation;

    const params = {
      companyCode: store.state.userContext.currentCompany,
      warehouseCode: store.state.userContext.currentWarehouse,
    };

    indexation.indiceId = indice.id;
    await this.restClient
      .post(`/Indexations`, indexation, { params })
      .then((result) => {
        if (result.status === 200) {
          data = result.data;
        } else {
          throw this.buildErrorsMessage(result.data);
        }
        data = result.data;
      })
      .catch((error) => {
        throw new Error(error);
      });

    return data;
  }

  /**
   * Mise a jour d'une indexation
   * @param idIndexation
   * @param indexation
   * @param indice
   * @returns
   */
  async updateIndexation(idIndexation: number, indexation: Indexation): Promise<void> {
    const params = {
      companyCode: store.state.userContext.currentCompany,
      warehouseCode: store.state.userContext.currentWarehouse,
    };
    await this.restClient
      .put(`/Indexations/${idIndexation}`, indexation, { params })
      .then((result) => {
        if (result.status !== 204) {
          throw this.buildErrorsMessage(result.data);
        }
      })
      .catch((error) => {
        throw new Error(error);
      });
  }

  /**
   * Suppression d'une indexation
   * @param idIndexation
   * @returns
   */
  async removeIndexation(idIndexation: number): Promise<void> {
    const params = {
      companyCode: store.state.userContext.currentCompany,
      warehouseCode: store.state.userContext.currentWarehouse,
    };

    await this.restClient
      .delete(`/Indexations/${idIndexation}`, { params })
      .then((result) => {
        if (result.status !== 204) {
          throw this.buildErrorsMessage(result.data);
        }
      })
      .catch((error) => {
        throw new Error(error);
      });
  }

  /**
   * Validation index
   * @param indexation
   * @param indice
   * @returns
   */
  async validateNewIndex(indexation: Indexation, indice?: Indice | null): Promise<{ errorText: string; isValid: boolean }> {
    return new Promise((resolve, reject) => {
      if (indice != null) {
        const params = { companyCode: store.state.userContext.currentCompany, warehouseCode: store.state.userContext.currentWarehouse };
        const body = { ...indexation, ...{ indiceId: indice.id } };
        this.restClient
          .post(`/IndexationIndexValidator`, body, { params })
          .then((response) => {
            if (response.status === 200) {
              resolve({ errorText: "", isValid: true });
            } else {
              reject(Object.values(response.data.errors).join());
            }
          })
          .catch((error) => {
            Vue.$log.error(error);
            reject("Erreur lors de l'appel a la validation Indice : " + error);
          });
      } else {
        reject(new Error("Indice non selectionné"));
      }
    });
  }

  /**
   * Validation Tiers
   * @param client
   * @param societe
   * @returns
   */
  async validateNewTiers(client: IndexationTiers, societe?: Societe | null): Promise<{ errorText: string; isValid: boolean }> {
    return new Promise((resolve, reject) => {
      if (societe != null) {
        const params = { companyCode: store.state.userContext.currentCompany, warehouseCode: store.state.userContext.currentWarehouse };
        const body = { ...client, ...{ societeId: societe.id } };
        this.restClient
          .post(`/IndexationTiersValidator`, body, { params })
          .then((response) => {
            if (response.status === 200) {
              resolve({ errorText: "", isValid: true });
            } else {
              reject(Object.values(response.data.errors).join());
            }
          })
          .catch((error) => {
            Vue.$log.error(error);
            reject("Erreur lors de l'appel a la validation Tiers : " + error);
          });
      } else {
        reject(new Error("Tiers non selectionné"));
      }
    });
  }

  /**
   * Validation Tarif
   * @param tarifGeneral
   * @param indexFamily
   * @returns
   */
  async validateNewTarif(tarifGeneral: TarifGeneral, indexFamily?: IndexFamily | null): Promise<{ errorText: string; isValid: boolean }> {
    return new Promise((resolve, reject) => {
      if (indexFamily != null) {
        const params = { companyCode: store.state.userContext.currentCompany, warehouseCode: store.state.userContext.currentWarehouse };
        const body = { TarifGeneralId: tarifGeneral.id, IndexFamilyId: indexFamily.id };

        this.restClient
          .post(`/IndexationTarifValidator`, body, { params })
          .then((response) => {
            if (response.status === 200) {
              resolve({ errorText: "", isValid: true });
            } else {
              reject(Object.values(response.data.errors).join());
            }
          })
          .catch((error) => {
            Vue.$log.error(error);
            reject("Erreur lors de l'appel a la validation Tarif : " + error);
          });
      } else {
        reject(new Error("Tarif non selectionné"));
      }
    });
  }

  /* Fin indice et indexation */

  /**
   * Recuperation de la liste des societes
   * @returns
   */
  async getListeSocietes(): Promise<{ data: Societe[]; itemsCount: number }> {
    let data = [] as Societe[];
    let itemsCount = 0;

    const params = { companyCode: store.state.userContext.currentCompany, warehouseCode: store.state.userContext.currentWarehouse };
    await this.restClient
      .get(`/Societes`, { params })
      .then((response) => {
        if (response.status == 200) {
          data = response.data;
          itemsCount = response.data.length;
        }
      })
      .catch((error) => {
        Vue.$log.error(error);
      });

    return { data, itemsCount };
  }

  /* Debut indexationTiers */

  /**
   * Recuperation des TiersDO
   * @param societe
   * @returns
   */
  async getListeTiers(societe: Societe): Promise<{ data: Tiers[]; itemsCount: number }> {
    let data = [] as Tiers[];
    let itemsCount = 0;

    const params = { companyCode: store.state.userContext.currentCompany, warehouseCode: store.state.userContext.currentWarehouse };
    await this.restClient
      .get(`/societes/${societe.id}/TiersDo`, { params })
      .then((response) => {
        if (response.status == 200) {
          data = response.data;
          itemsCount = response.data.length;
        }
      })
      .catch((error) => {
        Vue.$log.error(error);
      });

    return { data, itemsCount };
  }

  /**
   * Recuperation des indexFamily
   * @returns
   */
  async getListeIndexFamily(): Promise<{ data: IndexFamily[]; itemsCount: number }> {
    let data = [] as IndexFamily[];
    let itemsCount = 0;

    const params = { companyCode: store.state.userContext.currentCompany, warehouseCode: store.state.userContext.currentWarehouse };
    await this.restClient
      .get(`/IndexationsFamily`, { params })
      .then((response) => {
        if (response.status == 200) {
          data = response.data;
          itemsCount = response.data.length;
        }
      })
      .catch((error) => {
        Vue.$log.error(error);
      });

    return { data, itemsCount };
  }

  /**
   * Recuperation des indexationTiers
   * @param societe
   * @returns
   */
  async getListeClients(societe: Societe, requestOptions: ParamsOptions): Promise<{ data: IndexationTiers[]; itemsCount: number }> {
    let data = [] as IndexationTiers[];
    let itemsCount = 0;
    const params = {
      ...{ companyCode: store.state.userContext.currentCompany, warehouseCode: store.state.userContext.currentWarehouse },
      ...requestOptions,
    };

    await this.restClient
      .get(`/societes/${societe.id}/IndexationsTiers`, { params })
      .then((response) => {
        if (response.status == 200) {
          data = response.data;
          itemsCount = response.data.length;
        }
      })
      .catch((error) => {
        Vue.$log.error(error);
      });

    return { data, itemsCount };
  }

  /**
   * Ajout d'indexationTiers
   * @param client
   * @param societe
   * @returns
   */
  async addClient(client: IndexationTiers, societe: Societe): Promise<IndexationTiers> {
    let data: IndexationTiers = {} as IndexationTiers;
    const params = {
      companyCode: store.state.userContext.currentCompany,
      warehouseCode: store.state.userContext.currentWarehouse,
      societeId: societe.id,
    };
    await this.restClient
      .post(`/indexationTiers`, client, { params })
      .then((result) => {
        if (result.status === 200) {
          data = result.data;
        } else {
          throw this.buildErrorsMessage(result.data);
        }
        data = result.data;
      })
      .catch((error) => {
        throw new Error(error);
      });

    return data;
  }

  /**
   * Update d'indexationTiers
   * @param idClient
   * @param client
   */
  async updateClient(idClient: number, client: IndexationTiers): Promise<void> {
    const params = {
      companyCode: store.state.userContext.currentCompany,
      warehouseCode: store.state.userContext.currentWarehouse,
    };
    await this.restClient
      .put(`/indexationTiers/${idClient}`, client, { params })
      .then((result) => {
        if (result.status !== 204) {
          throw this.buildErrorsMessage(result.data);
        }
      })
      .catch((error) => {
        throw new Error(error);
      });
  }

  /**
   * Delete Ajout d'indexationTiers
   * @param idClient
   */
  async removeClient(idClient: number): Promise<void> {
    const params = {
      companyCode: store.state.userContext.currentCompany,
      warehouseCode: store.state.userContext.currentWarehouse,
    };

    await this.restClient
      .delete(`/indexationTiers/${idClient}`, { params })
      .then((result) => {
        if (result.status !== 204) {
          throw this.buildErrorsMessage(result.data);
        }
      })
      .catch((error) => {
        throw new Error(error);
      });
  }
  /* fin clients */

  /* Debut tarifs */
  /**
   * Recuperation de la liste des tarifs generaux
   * @param indexFamily
   * @param option
   * @returns
   */
  async getListeTarifGeneral(indexFamily: IndexFamily, option: ParamsOptions): Promise<{ data: TarifGeneral[]; itemsCount: number }> {
    let data = [] as TarifGeneral[];
    let itemsCount = 0;

    const params = {
      ...{ companyCode: store.state.userContext.currentCompany, warehouseCode: store.state.userContext.currentWarehouse },
      ...option,
    };
    await this.restClient
      .get(`/IndexationsFamily/${indexFamily.id}/TarifsGeneraux`, { params })
      .then((response) => {
        if (response.status == 200) {
          data = response.data;
          itemsCount = response.data.length;
        }
      })
      .catch((error) => {
        Vue.$log.error(error);
      });

    return { data, itemsCount };
  }

  /**
   * Recuperation un tarifs general par son id
   * @param id
   * @param indexFamily
   * @returns
   */
  async getTarifGeneral(id: number): Promise<TarifGeneral> {
    let data: TarifGeneral = {} as TarifGeneral;

    const params = {
      companyCode: store.state.userContext.currentCompany,
      warehouseCode: store.state.userContext.currentWarehouse,
    };
    await this.restClient
      .get(`/TarifGeneral/${id}`, { params })
      .then((response) => {
        if (response.status == 200) {
          data = response.data;
        }
      })
      .catch((error) => {
        Vue.$log.error(error);
      });
    return data;
  }

  /**
   * Recuperation de la liste des indexations tarifs
   * @param indexFamily
   * @returns
   */
  async getListeTarifs(indexFamily: IndexFamily): Promise<{ data: Tarif[]; itemsCount: number }> {
    let data = [] as Tarif[];
    let itemsCount = 0;

    const params = {
      companyCode: store.state.userContext.currentCompany,
      warehouseCode: store.state.userContext.currentWarehouse,
    };
    await this.restClient
      .get(`/IndexationsFamily/${indexFamily.id}/IndexationTarifs`, { params })
      .then((response) => {
        if (response.status == 200) {
          data = response.data;
          itemsCount = response.data.length;
        }
      })
      .catch((error) => {
        Vue.$log.error(error);
      });

    return { data, itemsCount };
  }

  /**
   * Ajout d'un indexations tarifs
   * @param tarif
   * @param indexFamily
   * @returns
   */
  async addTarif(tarif: Tarif, indexFamily: IndexFamily): Promise<Tarif> {
    let data: Tarif = {} as Tarif;

    const params = {
      companyCode: store.state.userContext.currentCompany,
      warehouseCode: store.state.userContext.currentWarehouse,
    };
    await this.restClient
      .post(`/IndexationsTarifs`, { indexFamilyId: indexFamily.id, tarifGeneralId: tarif.tarifGeneral.id }, { params })
      .then((result) => {
        if (result.status === 200) {
          data = result.data;
        } else {
          throw this.buildErrorsMessage(result.data);
        }
        data = result.data;
      })
      .catch((error) => {
        throw new Error(error);
      });

    return data;
  }

  /**
   * Update d'un indexations tarifs
   * @param idTarif
   * @param tarif
   */
  async updateTarif(idTarif: number, tarif: Tarif): Promise<void> {
    const params = {
      companyCode: store.state.userContext.currentCompany,
      warehouseCode: store.state.userContext.currentWarehouse,
    };
    await this.restClient
      .put(`/IndexationsTarifs/${idTarif}`, tarif, { params })
      .then((result) => {
        if (result.status !== 204) {
          throw this.buildErrorsMessage(result.data);
        }
      })
      .catch((error) => {
        throw new Error(error);
      });
  }
  /**
   * Suppression d'un indexations tarifs
   * @param idTarif
   */
  async removeTarif(idTarif: number): Promise<void> {
    const params = {
      companyCode: store.state.userContext.currentCompany,
      warehouseCode: store.state.userContext.currentWarehouse,
    };

    await this.restClient
      .delete(`/IndexationsTarifs/${idTarif}`, { params })
      .then((result) => {
        if (result.status !== 204) {
          throw this.buildErrorsMessage(result.data);
        }
      })
      .catch((error) => {
        throw new Error(error);
      });
  }

  /* fin tarifs */

  /* Debut indexation tiers calcul  */

  /**
   * Liste des societe tiers a selectionner pour mise a jours
   * @returns
   */
  async getSocieteTiers(): Promise<{ data: Societe[]; itemsCount: number }> {
    let data = [] as Indice[];
    let itemsCount = 0;

    const params = { companyCode: store.state.userContext.currentCompany, warehouseCode: store.state.userContext.currentWarehouse };

    await this.restClient
      .get(`/IndexationTiersDoSociete/GetIndexationTiersDoSociete`, { params })
      .then((response) => {
        if (response.status == 200) {
          data = response.data;
          itemsCount = response.data.length;
        }
      })
      .catch((error) => {
        Vue.$log.error(error);
      });

    return { data, itemsCount };
  }

  /**
   * Appel a la methode de simulation de calcul
   * @param selectedRowKeys
   * @param dateUpdate
   * @param onlyUpdate
   * @returns
   */
  async postSimulationCalcul(
    selectedRowKeys: number[],
    dateUpdate: Date,
    onlyUpdate: boolean
  ): Promise<{ data: CalculatedIndexation[]; itemsCount: number }> {
    let data = [] as CalculatedIndexation[];
    let itemsCount = 0;

    const params = {
      companyCode: store.state.userContext.currentCompany,
      warehouseCode: store.state.userContext.currentWarehouse,
    };

    await this.restClient
      .post(
        `/SimulationMiseAJoursLC/PostSimulationMiseAJoursLC`,
        { ids: selectedRowKeys, date: dateUpdate, onlyMaJ: onlyUpdate },
        { params }
      )
      .then((response) => {
        if (response.status == 200) {
          data = response.data;
          itemsCount = response.data.length;
        }
      })
      .catch((error) => {
        Vue.$log.error(error);
      });

    return { data, itemsCount };
  }

  /**
   * Appel a la methode pour calcul final
   * @param selectedRowKeys
   * @param dateUpdate
   * @param onlyUpdate
   * @returns
   */
  async postFinalCalcul(
    selectedRowKeys: number[],
    dateUpdate: Date,
    onlyUpdate: boolean
  ): Promise<{ data: CalculatedIndexation[]; itemsCount: number }> {
    return new Promise((resolve, reject) => {
      const params = {
        companyCode: store.state.userContext.currentCompany,
        warehouseCode: store.state.userContext.currentWarehouse,
      };

      this.restClient
        .post(`/MiseAJoursLC/PostMiseAJoursLC`, { ids: selectedRowKeys, date: dateUpdate, onlyMaJ: onlyUpdate }, { params })
        .then((response) => {
          if (response.status == 204) {
            resolve({
              data: response.data,
              itemsCount: response.data.length,
            });
          } else {
            throw new Error(response.data);
          }
        })
        .catch((error) => {
          Vue.$log.error(error);
          reject(new Error(`Error postFinalCalcul from Indexation API: ${error}`));
        });
    });
  }

  /* Fin indexation tiers calcul */

  /**
   * Constructeur de message d'erreurs
   * @param resultData
   * @returns
   */
  buildErrorsMessage(resultData: any): string {
    let listError = resultData.errors;
    let message = "";
    if (resultData.errors) {
      listError = resultData.errors;
      message = resultData.message;
    }
    let errorMessage = "";
    Object.entries(listError).forEach(([key, value]) => {
      errorMessage += `${key}: ${value}\n`;
    });

    listError = `${message}\n${errorMessage}`;

    return listError;
  }
}
